import { useState } from 'react'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { fmtBytes, KIND_LABEL } from '@/lib/format'
import { saveUrl } from '@/lib/zip'
import { RecordingPlayer } from './RecordingPlayer'
import type { ReviewDetail } from './types'

/** The frozen execution recording and files for this review, not the author's current uploads. */
export function PeerExecution({
  base,
  submission,
  active,
  example,
}: {
  example?: { recordingUrl: string }
  base: string
  submission: ReviewDetail['submission']
  active: boolean
}) {
  const recordings = submission.files.filter((file) => file.kind === 'video')
  const files = submission.files.filter((file) => file.kind !== 'video')
  const [recording, setRecording] = useState(recordings[0]?.file_id ?? '')
  const [error, setError] = useState('')
  const link = async (id: string) => (await api.get<{ url: string }>(`${base}/files/${id}`)).url
  async function download(id: string) {
    if (example) return
    setError('')
    try {
      const { url } = await api.get<{ url: string }>(`${base}/files/${id}`)
      saveUrl(url)
    } catch (err) {
      setError((err as Error).message)
    }
  }
  return (
    <div className="peer-execution">
      <div className="panel-section-head">
        <h3>Execution recording</h3>
        {recording && (
          <button className="link small" disabled={!!example} aria-label={`Download ${recordings.find((file) => file.file_id === recording)?.filename ?? 'the recording'}`} onClick={() => void download(recording)}>
            Download
          </button>
        )}
      </div>
      {recordings.length > 1 && (
        <div className="row">
          {recordings.map((file) => (
            <button
              className="ghost small"
              key={file.file_id}
              aria-pressed={recording === file.file_id}
              onClick={() => { setRecording(file.file_id); setError('') }}
            >
              {file.filename}
            </button>
          ))}
        </div>
      )}
      {!recording ? (
        <p className="muted small">No recording was submitted.</p>
      ) : example ? (
        <RecordingPlayer src={example.recordingUrl} active={active} />
      ) : (
        <RecordingPlayer key={`${base}/${recording}`} load={() => link(recording)} onDownload={() => void download(recording)} active={active} />
      )}
      {error && (
        <p className="note err" role="alert">
          {error}
        </p>
      )}
      {submission.task.submission?.notes && (
        <section className="peer-execution-notes">
          <h3>Execution notes</h3>
          <p className="prewrap">{submission.task.submission.notes}</p>
        </section>
      )}
      <section className="peer-execution-files">
        <h3>Submission files</h3>
        {submission.task.submission?.empty_start && (
          <p className="muted small">This task starts from an empty document.</p>
        )}
        {!files.length && <p className="muted small">No additional files.</p>}
        <ul className="peer-file-list">
          {files.map((file) => (
            <li key={file.file_id}>
              <Icon name="file" size={18} />
              <div>
                <button className="link" disabled={!!example} onClick={() => void download(file.file_id)}>
                  {file.filename}
                </button>
                <p className="muted small">
                  {KIND_LABEL[file.kind] ?? file.kind.replaceAll('_', ' ')} · {fmtBytes(file.size)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
